import { Box, Grid, Paper, useTheme } from "@mui/material";
import { FC, useMemo } from "react";
import { Link, Outlet } from "react-router-dom";
import { Permissions, Tenant } from "../../api/dtos";
import GridActions from "../../components/GridActions";
import SetLinks from "../../components/layout/SetLinks";
import { StyledButton } from "../../components/StyledButton";
import CollapsibleAppTable from "../../components/tables/CollapsibleAppTable";
import type { AppColumn } from "../../components/tables/AppTable.types";
import { DefaultProjectCreateLink } from "../../contexts/layouts/LinkContext";
import TenantsContext from "../../contexts/TenantsContext";
import useTenants from "../../hooks/data/useTenants";

function requiredTenantId(row: Tenant): number {
    if (row.id === undefined || row.id === null) {
        throw new Error("Tenant is missing its ID.");
    }

    return row.id;
}

const useTenantColumns = () => {
    const tenantColumns = useMemo(() => {
        const columns: AppColumn<Tenant>[] = [
            {
                id: "name",
                label: "Name",
                scalarType: "text",
                accessor: (row) => row.name,
                flex: 1,
            },
            {
                id: "rank",
                label: "Rank",
                scalarType: "number",
                accessor: (row) => row.rank,
                flex: 1,
            },
            {
                id: "actions",
                label: "",
                headerLabel: "",
                kind: "action",
                scalarType: "text",
                accessor: (row) => String(row.id ?? ""),
                width: 100,
                cell: ({ row }) => (
                    <GridActions
                        editPermission={Permissions.AdminAccess}
                        editUrl={`/admin/stage-precincts/edit/${requiredTenantId(row)}`}
                    />
                ),
            },
        ];

        return columns;
    }, []);

    return tenantColumns;
};

const Agents: FC = () => {
    const columns = useTenantColumns();

    const theme = useTheme();

    const context = useTenants();

    const { data: tenants, loading: tenantsLoading } = context;

    const exportMetadata = useMemo(
        () => ({
            fileName: `Tenants`,
            exportTitle: `Tenants`,
            includeDateTime: true,
        }),
        [],
    );

    return (
        <TenantsContext.Provider value={context}>
            <Outlet />
            <SetLinks createProjectLink={DefaultProjectCreateLink} stageLinkEnabled={false} lotLinkEnabled={false} />
            <Grid container gap={theme.spacing(2)} justifyContent={"end"} marginBottom={theme.spacing(2)}>
                <StyledButton
                    variant="contained"
                    //@ts-expect-error
                    component={Link}
                    to={`/admin/tenants/create`}
                    state={{
                        back: true,
                    }}
                >
                    Add Tenant
                </StyledButton>
            </Grid>
            <Paper>
                <Box>
                    <CollapsibleAppTable
                        density="compact"
                        startCollapsed={false}
                        title="Tenants"
                        loading={tenantsLoading}
                        rows={tenants ?? []}
                        columns={columns}
                        getRowId={requiredTenantId}
                        columnControls={{
                            menu: true,
                        }}
                        filterControls
                        export={exportMetadata}
                    />
                </Box>
            </Paper>
        </TenantsContext.Provider>
    );
};

export default Agents;
